import { useEffect, useMemo, useState, useCallback } from 'react'
import { View, Text, TextInput, TouchableOpacity, FlatList, Modal, ActivityIndicator, StyleSheet } from 'react-native'
import { useSafeAreaInsets } from 'react-native-safe-area-context'
import { useTranslation } from 'react-i18next'
import Ionicons from '@expo/vector-icons/Ionicons'
import { supabase } from '../../lib/supabase'
import { fonts, radius } from '../../lib/theme'
import { useThemeColors } from '../../lib/useThemeColors'
import { BibTag, NationalFlag } from '../../components/Pick/ui'
import RiderFace from '../../components/RiderFace'
import { loadRiderInfo } from '../../components/Pick/riderInfo'

// Start list della gara consultabile da tutti (cerca per dorsale o cognome): serve a riconoscere un corridore mentre si guarda la gara.
// Legge map_start_list (comprende i ritirati, is_dnf, mostrati in grigio) e l'anagrafica con loadRiderInfo.
export async function loadStartlist(idRace) {
  const { data } = await supabase.from('map_start_list').select('id_rider, bib, is_dnf').eq('id_race', idRace)
  const rows = data ?? []
  if (!rows.length) return []
  const info = await loadRiderInfo(rows.map(r => r.id_rider))
  return rows.map(r => ({ ...info[r.id_rider], id: r.id_rider, bib: r.bib, dnf: !!r.is_dnf })).filter(r => r.label)
    .sort((a, b) => (a.bib ?? 1e9) - (b.bib ?? 1e9) || a.label.localeCompare(b.label))
}

export default function ModaleStartlist({ visible, idRace, raceName, onClose }) {
  const { t } = useTranslation()
  const tc = useThemeColors()
  const styles = useMemo(() => makeStyles(tc), [tc])
  const insets = useSafeAreaInsets()
  const [rows, setRows] = useState(null)
  const [search, setSearch] = useState('')

  useEffect(() => {
    if (!visible || !idRace) return
    let cancelled = false
    setRows(null); setSearch('')
    loadStartlist(idRace).then(r => { if (!cancelled) setRows(r) }).catch(() => { if (!cancelled) setRows([]) })
    return () => { cancelled = true }
  }, [visible, idRace])

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase()
    if (!rows || !q) return rows ?? []
    return rows.filter(r => String(r.bib ?? '') === q || String(r.bib ?? '').startsWith(q) || r.label.toLowerCase().includes(q) || (r.teamName ?? '').toLowerCase().includes(q))
  }, [rows, search])

  const renderItem = useCallback(({ item }) => (
    <View style={[styles.row, item.dnf && { opacity: 0.45 }]}>
      <View style={{ width: 44, alignItems: 'center' }}><BibTag bib={item.bib} /></View>
      <View style={{ marginHorizontal: 10 }}><RiderFace avatar={item.avatar} kit={item.kit} size={38} /></View>
      <View style={{ flex: 1 }}>
        <Text style={styles.name} numberOfLines={1}>{item.label}</Text>
        <Text style={styles.team} numberOfLines={1}>{item.dnf ? `${t('pick.startlist_dnf')} · ` : ''}{item.teamName ?? ''}</Text>
      </View>
      <NationalFlag code={item.id_national} />
    </View>
  ), [styles, t])

  return (
    <Modal visible={visible} animationType="slide" presentationStyle="pageSheet" onRequestClose={onClose}>
      <View style={styles.container}>
        <View style={styles.header}>
          <View style={{ flex: 1 }}>
            <Text style={styles.title}>{t('pick.startlist_title')}</Text>
            {!!raceName && <Text style={styles.sub} numberOfLines={1}>{raceName}{rows ? ` · ${rows.length}` : ''}</Text>}
          </View>
          <TouchableOpacity onPress={onClose} accessibilityLabel={t('common.close')} hitSlop={10}>
            <Ionicons name="close" size={24} color={tc.text} />
          </TouchableOpacity>
        </View>
        <View style={styles.searchBox}>
          <Ionicons name="search" size={16} color={tc.textMuted} />
          <TextInput style={styles.input} value={search} onChangeText={setSearch} placeholder={t('pick.startlist_search')}
            placeholderTextColor={tc.textDim} autoCorrect={false} autoCapitalize="none" />
          {search ? <TouchableOpacity onPress={() => setSearch('')}><Ionicons name="close-circle" size={16} color={tc.textMuted} /></TouchableOpacity> : null}
        </View>
        {rows === null ? <ActivityIndicator style={{ marginTop: 32 }} color={tc.rosa} /> : (
          <FlatList data={filtered} keyExtractor={r => String(r.id)} renderItem={renderItem} keyboardShouldPersistTaps="handled"
            contentContainerStyle={{ paddingBottom: insets.bottom + 16 }}
            ListEmptyComponent={<Text style={styles.empty}>{rows.length ? t('pick.no_rider_found') : t('pick.startlist_empty')}</Text>} />
        )}
      </View>
    </Modal>
  )
}

const makeStyles = (tc) => StyleSheet.create({
  container: { flex: 1, backgroundColor: tc.bg },
  header: { flexDirection: 'row', alignItems: 'center', paddingHorizontal: 16, paddingTop: 16, paddingBottom: 8 },
  title: { fontSize: 22, fontFamily: fonts.condensedBold, color: tc.text, textTransform: 'uppercase' },
  sub: { fontSize: 13, color: tc.textMuted },
  searchBox: { flexDirection: 'row', alignItems: 'center', gap: 8, marginHorizontal: 16, marginBottom: 8, paddingHorizontal: 12, height: 40, borderRadius: radius.md, borderWidth: 1, borderColor: tc.border, backgroundColor: tc.card },
  input: { flex: 1, color: tc.text, fontSize: 15 },
  row: { flexDirection: 'row', alignItems: 'center', paddingHorizontal: 12, paddingVertical: 7, borderBottomWidth: StyleSheet.hairlineWidth, borderBottomColor: tc.border },
  name: { fontSize: 14, color: tc.text, fontFamily: fonts.condensedBold },
  team: { fontSize: 12, color: tc.textMuted },
  empty: { textAlign: 'center', color: tc.textMuted, padding: 24 },
})
